/**
 * Sheet — the light card as a modal surface.
 *
 * `Modal` is dark by design and stays for the dark tooling (the debug panel's family). This is the
 * other half of the app: the surfaces a player reads while stopped, in the same white-card language
 * as ExplorePanel.
 *
 * IT KEEPS THE PORTAL, and that is not a detail — `Modal`'s own note says why: a transformed
 * ancestor makes `position: fixed` resolve against that ancestor instead of the viewport, so a sheet
 * rendered inside one lands off-centre or off-screen. Rendering into `document.body` is what makes
 * the centring mean what it says.
 */

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { PANEL_SHADOW } from './tokens';

interface SheetProps {
  title: string;
  onClose: () => void;
  /** Buttons, laid out by the caller (`mr-auto` pushes one to the left). */
  footer?: ReactNode;
  children: ReactNode;
}

export function Sheet({ title, onClose, footer, children }: SheetProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`w-[min(94vw,32rem)] max-h-[88dvh] flex flex-col bg-white rounded-3xl ${PANEL_SHADOW}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="px-5 pt-4 pb-2 shrink-0">
          <h2 className="text-sm font-bold text-neutral-800">{title}</h2>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-1 scrollbar-compact">{children}</div>
        {footer && (
          <div className="px-5 py-3 shrink-0 flex items-center justify-end gap-2 border-t border-neutral-100">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
